import React, { useState } from 'react';
import { ChevronDown, Plus, Minus, Trash } from 'lucide-react';
import { useLanguage } from '../../hooks/useLanguage';

const duaSpecificationTranslations = {
  specifyDUAs: {
    eng: 'Specify DUAs and Partidas',
    esp: 'Especificar DUAs y Partidas'
  },
  optional: {
    eng: '(Optional)',
    esp: '(Opcional)'
  },
  numberOfDUAs: {
    eng: 'Number of DUAs',
    esp: 'Número de DUAs'
  },
  dua: {
    eng: 'DUA',
    esp: 'DUA'
  },
  partidas: {
    eng: 'Partidas',
    esp: 'Partidas'
  },
  numberOfPartidas: {
    eng: 'Number of Partidas',
    esp: 'Número de Partidas'
  },
  addDUA: {
    eng: 'Add DUA',
    esp: 'Agregar DUA'
  },
  removeDUA: {
    eng: 'Remove DUA',
    esp: 'Eliminar DUA'
  },
  additionalContext: {
    eng: 'Additional Context',
    esp: 'Contexto Adicional'
  }
};

function getDUASpecificationTranslation(key, isSpanish) {
  if (!duaSpecificationTranslations[key]) return key;
  return isSpanish ? duaSpecificationTranslations[key].esp : duaSpecificationTranslations[key].eng;
}

export const DUASpecification = ({ onSpecifyAmountChange, onAdditionalContextChange }) => {
  const [isSpanish] = useLanguage();
  const [isExpanded, setIsExpanded] = useState(false);
  const [duaCount, setDuaCount] = useState(0);
  const [partidasCounts, setPartidasCounts] = useState([]);
  const [additionalContext, setAdditionalContext] = useState('');

  const handleDuaCountChange = (newCount) => {
    const count = Math.max(0, Math.min(100, newCount)); // Limit to 0-100
    setDuaCount(count);
    
    const newPartidasCounts = [...partidasCounts];
    if (count > newPartidasCounts.length) {
      while (newPartidasCounts.length < count) {
        newPartidasCounts.push(1);
      }
    } else if (count < newPartidasCounts.length) {
      newPartidasCounts.splice(count);
    }
    
    setPartidasCounts(newPartidasCounts);
    updateSpecifyAmount(newPartidasCounts);
  };

  const handlePartidasChange = (duaIndex, newCount) => {
    const count = Math.max(1, Math.min(100, newCount));
    const newPartidasCounts = [...partidasCounts];
    newPartidasCounts[duaIndex] = count;
    setPartidasCounts(newPartidasCounts);
    updateSpecifyAmount(newPartidasCounts);
  };

  const updateSpecifyAmount = (counts) => {
    if (onSpecifyAmountChange) {
      if (counts.length > 0) {
        onSpecifyAmountChange(counts);
      } else {
        onSpecifyAmountChange([]);
      }
    }
  };

  const handleAddDUA = () => {
    handleDuaCountChange(duaCount + 1);
  };

  const handleRemoveDUA = (index) => {
    const newPartidasCounts = partidasCounts.filter((_, i) => i !== index);
    setPartidasCounts(newPartidasCounts);
    setDuaCount(newPartidasCounts.length);
    updateSpecifyAmount(newPartidasCounts);
  };

  return (
    <div className="mt-4 bg-white border-container border-gray-200 rounded-lg shadow-sm overflow-hidden transition-all duration-200">
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="button-no-hover-effect w-full flex items-center justify-between p-4 hover:bg-gray-50 transition-colors duration-150"
      >
        <div className="flex items-center gap-2">
          <span className="font-semibold text-gray-700">
            {getDUASpecificationTranslation('specifyDUAs', isSpanish)}
          </span>
          <span className="text-sm text-gray-500">
            {getDUASpecificationTranslation('optional', isSpanish)}
          </span>
          {duaCount > 0 && !isExpanded && (
            <span className="text-sm text-default font-medium ml-2">
              ({duaCount} {getDUASpecificationTranslation('dua', isSpanish)}
              {!isSpanish && duaCount !== 1 ? 's' : ''})
            </span>
          )}
        </div>
        <ChevronDown
          className={`size-5 text-gray-500 transition-transform duration-200 ${
            isExpanded ? 'rotate-180' : ''
          }`}
        />
      </button>

      {isExpanded && (
        <div className="border-t border-gray-200 p-4 space-y-3 transition-all duration-200">
          <div className="flex items-center gap-3">
            <label className="text-sm font-medium text-gray-700 min-w-[140px]">
              {getDUASpecificationTranslation('numberOfDUAs', isSpanish)}:
            </label>
            <div className="flex items-center gap-2">
              <input
                type="number"
                min="0"
                max="20"
                value={duaCount}
                onChange={(e) => handleDuaCountChange(parseInt(e.target.value) || 0)}
                className="w-20 min-w-48 max-w-48 px-3 py-1.5 text-center border-container border-gray-300 rounded-full focus:outline-none focus:ring-1 focus:ring-default focus:border-transparent"
              />
              <button
                onClick={() => handleDuaCountChange(duaCount - 1)}
                disabled={duaCount === 0}
                className="p-2 rounded-full bg-gray-100 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                aria-label={getDUASpecificationTranslation('removeDUA', isSpanish)}
              >
                <Minus className="size-4 text-gray-600" />
              </button>
              <button
                onClick={handleAddDUA}
                disabled={duaCount >= 20}
                className="p-2 rounded-full bg-gray-100 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                aria-label={getDUASpecificationTranslation('addDUA', isSpanish)}
              >
                <Plus className="size-4 text-gray-600" />
              </button>
            </div>
          </div>

          {duaCount > 0 && (
            <div className="mt-4 space-y-3">
              <div className="text-sm font-medium text-gray-700 mb-2">
                {getDUASpecificationTranslation('numberOfPartidas', isSpanish)}:
              </div>
              <div className="space-y-2 max-h-64 overflow-y-auto pr-2">
                {partidasCounts.map((partidasCount, index) => (
                  <div
                    key={index}
                    className="flex items-center gap-3 p-3 bg-gray-50 rounded-md hover:bg-gray-100 transition-colors justify-between"
                  >
                    <div className="flex items-center gap-2">
                    <label className="text-sm font-medium text-gray-700 min-w-[80px]">
                      {getDUASpecificationTranslation('dua', isSpanish)} {index + 1}:
                    </label>
                    <div className="flex items-center gap-2 flex-1">
                      <input
                        type="number"
                        min="1"
                        max="100"
                        value={partidasCount}
                        onChange={(e) => handlePartidasChange(index, parseInt(e.target.value) || 1)}
                        className="min-w-48 max-w-48 flex-1 px-3 py-1.5 text-center border-container border-gray-300 rounded-full focus:outline-none focus:ring-1 focus:ring-default focus:border-transparent"
                      />
                      <button
                        onClick={() => handlePartidasChange(index, partidasCount - 1)}
                        disabled={partidasCount <= 1}
                        className="p-2 rounded-full bg-gray-200 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                      >
                        <Minus className="size-4 text-gray-600" />
                      </button>
                      <button
                        onClick={() => handlePartidasChange(index, partidasCount + 1)}
                        disabled={partidasCount >= 100}
                        className="p-2 rounded-full bg-gray-200 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                      >
                        <Plus className="size-4 text-gray-600" />
                      </button>
                    </div>
                    <span className="text-sm text-gray-500 min-w-[60px]">
                      {getDUASpecificationTranslation('partidas', isSpanish)}
                    </span>
                    </div>
                    {duaCount > 1 && (
                      <button
                        onClick={() => handleRemoveDUA(index)}
                        className="p-1.5 rounded-full hover:bg-red-100 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                        aria-label={`Remove ${getDUASpecificationTranslation('dua', isSpanish)} ${index + 1}`}
                      >
                        <Trash className="size-4 stroke-red-500" />
                      </button>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {duaCount === 0 && (
            <p className="text-sm text-gray-500 italic text-left py-2">
              {isSpanish 
                ? 'No se han especificado DUAs. El sistema procesará los documentos automáticamente.'
                : 'No DUAs specified. The system will process documents automatically.'}
            </p>
          )}

          <div className="mt-4 pt-4 border-t border-gray-200">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {getDUASpecificationTranslation('additionalContext', isSpanish)}:
            </label>
            <textarea
              value={additionalContext}
              onChange={(e) => {
                const newValue = e.target.value;
                setAdditionalContext(newValue);
                if (onAdditionalContextChange) {
                  onAdditionalContextChange(newValue);
                }
              }}
              className="w-full px-3 py-2 border-container border-gray-300 rounded-lg focus:outline-none focus:ring-1 focus:ring-default focus:border-transparent resize-y min-h-[100px]"
              placeholder={isSpanish ? 'Ingrese contexto adicional...' : 'Enter additional context...'}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default DUASpecification;

